import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { ARTICLES } from "../content/blog";

const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const ArticleCard = ({ a, big = false }) => (
  <Link
    to={`/blog/${a.slug}`}
    className="group bg-[#0c0c0c] h-full flex flex-col hover:bg-[#101010] transition-colors"
    data-testid={`blog-card-${a.slug}`}
  >
    <div className={`overflow-hidden ${big ? "aspect-[16/8]" : "aspect-[16/9]"}`}>
      <img src={a.hero.src} alt={a.hero.alt} loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-[1.03] transition-all duration-500" />
    </div>
    <div className="p-6 sm:p-7 flex flex-col flex-1">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] border border-white/20 text-zinc-400 px-2 py-0.5">{a.category}</span>
        {a.pillar && <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] border border-volt/60 text-volt px-2 py-0.5">Pillar Guide</span>}
      </div>
      <h3 className={`font-head font-extrabold uppercase tracking-tight leading-tight group-hover:text-volt transition-colors mb-3 ${big ? "text-2xl sm:text-3xl" : "text-xl"}`}>
        {a.h1}
      </h3>
      <p className="text-zinc-400 text-sm leading-relaxed mb-5 line-clamp-3">{a.metaDescription}</p>
      <p className="mt-auto flex items-center gap-4 font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        <span className="flex items-center gap-1.5"><Clock size={11} /> {a.readTime} min read</span>
        <span>{fmtDate(a.dateModified)}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-volt opacity-0 group-hover:opacity-100 transition-opacity">Read <ArrowRight size={12} /></span>
      </p>
    </div>
  </Link>
);

export default function Blog() {
  const pillars = ARTICLES.filter((a) => a.pillar);
  const sims = ARTICLES.filter((a) => !a.pillar && a.category === "Simulations");
  const classes = ARTICLES.filter((a) => !a.pillar && a.category === "Classes & Booking");

  return (
    <div data-testid="blog-page" className="pt-16">
      <Seo
        title="HYROX Blog — Dallas Training Guides & Sim Recaps | HyroxNinja"
        description="Straight answers on HYROX in Dallas: what simulations and classes are, what they cost, how sim day runs, results recaps and how to book. Written and reviewed by Dallas HYROX athletes."
      />
      <section className="relative overflow-hidden">
        <img src="/dps/poster-group.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
          <p className="eyebrow mb-4">The Hyrox Ninja Journal</p>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
            <MaskedLine delay={0.1}>Straight Answers.</MaskedLine>
            <MaskedLine delay={0.25} className="text-volt">Dallas Tested.</MaskedLine>
          </h1>
          <p className="text-zinc-400 max-w-2xl leading-relaxed">
            Guides on simulations, classes, costs and booking, plus recaps and results from
            the Dallas floor. Written by Daniel Rivas, reviewed by Desmond Jasper, and tied
            to real sessions you can actually book.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="blog-pillars-section">
        <Reveal>
          <p className="eyebrow mb-4">Start Here — The Pillar Guides</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-volt/40">
          {pillars.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.06} className="h-full">
              <ArticleCard a={a} big />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="blog-simulations-section">
        <Reveal>
          <p className="eyebrow mb-4">Simulations</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {sims.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.04} className="h-full">
              <ArticleCard a={a} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="blog-classes-section">
        <Reveal>
          <p className="eyebrow mb-4">Classes & Booking</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {classes.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.04} className="h-full">
              <ArticleCard a={a} />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
